import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PeriodoMensualComponent } from '../../../shared/components/periodo-mensual/periodo-mensual.component';
import { PeriodoTrimestralComponent } from '../../../shared/components/periodo-trimestral/periodo-trimestral.component';
import { PeriodoSemestralComponent } from '../../../shared/components/periodo-semestral/periodo-semestral.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { Router } from '@angular/router';
import { RegistroService } from 'src/app/registro.service';
import Swal from 'sweetalert2';
import { ReporteIndService } from 'src/app/reporte/services/reporte-ind.service';
import { firstValueFrom } from 'rxjs';

export type MonthOption = { name: string; number: number };
export type QuarterOption = { name: string; number: number };
export type SemesterOption = { name: string; number: number };

export type PeriodoSeleccionadoModal = {
    year: number;
    month: MonthOption | null;
    quarter: QuarterOption | null;
    semester: SemesterOption | null;
    type: string;
};

@Component({
    selector: 'app-modal-periodo',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        ButtonComponent,
        PeriodoMensualComponent,
        PeriodoTrimestralComponent,
        PeriodoSemestralComponent,
    ],
    templateUrl: './modal-periodo.component.html',
    styleUrls: [
        './modal-periodo.component.css',
        '../../../components/view-succes/view-succes.component.css',
    ],
})
export class ModalPeriodoComponent implements OnInit {
    @Input() mensaje =
        'Por favor seleccione el año y mes en el que va a diligenciar la información de ventas e inventarios de los productos';
    @Input() years: number[] = [];
    @Input() months: MonthOption[] = [];
    @Input() quarters: QuarterOption[] = [];
    @Input() semesters: SemesterOption[] = [];
    @Input() minDate: string | null = null;
    /** Si es true, no navega: emite `periodoSeleccionado` y cierra (p. ej. filtro histórico). */
    @Input() soloSeleccionarPeriodo = false;
    @Input() labelBotonConfirmar = 'Crear Reporte';
    @Input() type: string = 'start';

    @Output() confirmar = new EventEmitter<{ year: number; month: MonthOption }>();
    @Output() cerrar = new EventEmitter<void>();
    @Output() periodoSeleccionado = new EventEmitter<PeriodoSeleccionadoModal>();

    displayIdl = false;
    displayIdlConfirm = false;
    mensajeConfirm = '';
    nombreBtnVolver = 'Volver';
    selectedYear: number | null = null;
    selectedMonth: MonthOption | null = null;
    selectedQuarter: QuarterOption | null = null;
    selectedSemester: SemesterOption | null = null;
    errorMessages: string[] = [];
    caracterizacionComercializador: any;

    constructor(
                private readonly registroService: RegistroService,
                private readonly reporteService: ReporteIndService,
                private readonly router: Router) {}

    ngOnInit() {
        this.caracterizacionComercializador = this.registroService.caracterizacionComercializador;
    }

    open(): void {
        this.displayIdl = true;
    }

    closeReport() {
        this.displayIdl = false;
        this.displayIdlConfirm = false;
        this.errorMessages = [];
        this.cerrar.emit();
    }

    volverConfirm(): void {
        this.displayIdlConfirm = false;
    }

    continuar(): void {
        this.emitPeriodoSeleccionado();
        this.displayIdlConfirm = false;
        this.closeReport();
    }

    private getPeriodoParaApi(): string | null {
        const tam = this.caracterizacionComercializador?.tamanoComercializador;
        if (tam === 'Grande' && this.selectedMonth) {
            return this.selectedMonth.number.toString();
        }
        if (tam === 'Mediano' && this.selectedQuarter) {
            return `${this.selectedQuarter.number}T`;
        }
        if (tam === 'Pequeño' && this.selectedSemester) {
            return `${this.selectedSemester.number}S`;
        }
        return null;
    }

    private emitPeriodoSeleccionado(): void {
        const tam = this.caracterizacionComercializador?.tamanoComercializador;
        if (tam === 'Grande' && this.selectedMonth) {
            this.periodoSeleccionado.emit({
                year: this.selectedYear!,
                month: this.selectedMonth,
                quarter: null,
                semester: null,
                type: this.type,
            });
            return;
        }
        if (tam === 'Mediano' && this.selectedQuarter) {
            this.periodoSeleccionado.emit({
                year: this.selectedYear!,
                month: null,
                quarter: this.selectedQuarter,
                semester: null,
                type: this.type,
            });
            return;
        }
        if (tam === 'Pequeño' && this.selectedSemester) {
            this.periodoSeleccionado.emit({
                year: this.selectedYear!,
                month: null,
                quarter: null,
                semester: this.selectedSemester,
                type: this.type,
            });
        }
    }

    async validarAntesDeContinuar(periodoApi: string): Promise<boolean> {
        try {
            const result = await firstValueFrom(
                this.reporteService.validarReemplazo({
                    agenteVigiladoId: Number(
                        this.registroService.empresaSelected,
                    ),
                    anio: Number(this.selectedYear),
                    periodo: periodoApi,
                }),
            );
            return result;
        } catch (error: any) {
            await Swal.fire({
                icon: 'error',
                title: 'No es posible reemplazar el reporte',
                text:
                    error?.error?.message ||
                    'El reporte no puede ser reemplazado',
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });
            return false;
        }
    }

    async submit(): Promise<void> {
        this.errorMessages = [];

        if (!this.selectedYear) {
            this.errorMessages.push('El año es requerido.');
            return;
        }

        const tam = this.caracterizacionComercializador?.tamanoComercializador;
        if (tam === 'Grande' && !this.selectedMonth) {
            this.errorMessages.push('El mes es requerido.');
            return;
        }
        if (tam === 'Mediano' && !this.selectedQuarter) {
            this.errorMessages.push('El trimestre es requerido.');
            return;
        }
        if (tam === 'Pequeño' && !this.selectedSemester) {
            this.errorMessages.push('El semestre es requerido.');
            return;
        }

        const periodoApi = this.getPeriodoParaApi();
        if (!periodoApi) {
            this.errorMessages.push('Seleccione un período válido.');
            return;
        }

        if (this.soloSeleccionarPeriodo) {
            this.emitPeriodoSeleccionado();
            this.closeReport();
            return;
        }

        try {
            const reportes = await this.reporteService.getReporteDate(
                this.selectedYear.toString(),
                periodoApi,
            );
            const existe =
                Array.isArray(reportes) && reportes.length > 0;

            if (!existe) {
                this.emitPeriodoSeleccionado();
                this.closeReport();
                return;
            }

            const validacion = await this.validarAntesDeContinuar(periodoApi);
            if (!validacion) {
                this.displayIdlConfirm = false;
                this.displayIdl = false;
                return;
            }

            this.mensajeConfirm =
                'El reporte para esta fecha ya existe, ¿Seguro que quiere reemplazarlo?';
            this.displayIdlConfirm = true;
        } catch (error) {
            console.error(error);
        }
    }
}

